// Third-party
import React from 'react';

// Components
import classNames from 'classnames';
import CollapsibleSimpleTable from '@/views/components/tables/CollapsibleSimpleTable';
import StackedBar from '../StackedBar';

// Utils
import { DEFAULT_RISK_COLORS } from '@/utils/risk-profiles';
import { formatMessage, formatPercentage } from '@/utils/i18n';

// Types
import { formatRiskProfileChartData, formatRiskProfileTableData } from '@/utils/risk-profile-rule-visualizer';
import { RiskProfileRule, RiskProfileRuleData } from '@/types/risk-profiles';
import useThemeVersion from '@/hooks/useThemeVersion';

interface Props {
  rule: RiskProfileRule;
  singleRuleLayout?: boolean;
  ruleData: RiskProfileRuleData[];
  targetLabel?: React.ReactNode;
  actualLabel?: React.ReactNode;
  hideConfig?: {
    hideRuleLabel: boolean;
    hideBars: boolean;
    hideTable: boolean;
  };
}

const RiskProfileRuleVisualizer = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  const { rule, singleRuleLayout, ruleData, targetLabel, actualLabel, hideConfig } = props;
  const showRuleLabel = !hideConfig?.hideRuleLabel;
  const showBars = !hideConfig?.hideBars;
  const showTable = !hideConfig?.hideTable;

  if (ruleData.length === 0) {
    return null;
  }

  const otherRule = ruleData.find(data => data.key === 'RISK_OTHER');
  const otherAlloc = otherRule ? otherRule.value : null;

  const { actualData, targetData } = formatRiskProfileChartData(rule, ruleData, singleRuleLayout, targetLabel, actualLabel);
  const legendData = formatRiskProfileTableData(rule, ruleData, targetLabel, actualLabel);

  return (
    <div className={classNames('risk-profile-rule-visualizer', { 'single-rule': singleRuleLayout, light: isLightVersion })}>
      {showRuleLabel && <h1 className="rule-title">{formatMessage(`RiskProfile.Rules.Titles.${rule.ruleType}`)}</h1>}

      {showBars && (
        <div className="risk-profile-bars">
          <StackedBar data={targetData} />

          {actualData && (
            <>
              <StackedBar data={actualData} />
              {otherAlloc != null && otherAlloc > 0 && (
                <div className="risk-legend-other">
                  <div className="profile-legend-color" style={{ backgroundColor: DEFAULT_RISK_COLORS[rule.ruleType].RISK_OTHER }} />
                  <span>{`${formatMessage('Common.DataUnavailable')} (${formatPercentage(otherAlloc, 1)})`}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {showTable && <CollapsibleSimpleTable data={legendData} />}
    </div>
  );
};

export default RiskProfileRuleVisualizer;
